import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Circle,
  ExternalLink,
  Flame,
  GraduationCap,
  Home,
  Loader2,
  Save,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";

import { useNavigate } from "react-router-dom";

import {
  formatServiceNumber,
  useTrainingSession,
} from "../../context/TrainingSessionContext";

const TASKS = [
  {
    id: "task1",
    number: 1,
    title: "About Your Squadron",
    short: "Squadron details",
  },
  {
    id: "task2",
    number: 2,
    title: "Cadet Portal",
    short: "Cadet Portal",
  },
  {
    id: "task3",
    number: 3,
    title: "Squadron Do’s and Don’ts",
    short: "Standards",
  },
  {
    id: "task4",
    number: 4,
    title: "Keeping Safe in the RAF Air Cadets",
    short: "Keeping safe",
  },
  {
    id: "task5",
    number: 5,
    title: "Health and Safety Induction",
    short: "Health & Safety",
  },
  {
    id: "task6",
    number: 6,
    title: "Fire Safety Induction",
    short: "Fire safety",
  },
  {
    id: "task7",
    number: 7,
    title: "Initial Activities",
    short: "Activities",
  },
  {
    id: "task8",
    number: 8,
    title: "About Your Uniform",
    short: "Uniform",
  },
];

const DO_ITEMS = [
  "When issued, carry your F3822 Record of Service book with you.",
  "Wear the correct uniform and be smart.",
  "Be good mannered and polite.",
  "Use the correct form of address for staff and cadet NCOs.",
  "Salute when entering the Commanding Officer’s office when in uniform.",
  "Volunteer for and attend as many activities as you can.",
  "Make sure you know where you have to be and when.",
  "Carry out instructions quickly, quietly and efficiently.",
  "Carry a notebook and pen and use them.",
  "Use Cadet Portal to tell staff if you have to miss a parade evening.",
  "Tell staff immediately if you have to drop out of an event.",
  "Pay any money that you owe on time.",
  "Look after equipment you borrow and return it when finished.",
  "Ask questions and ask for help if there is anything you do not understand.",
];

const DONT_ITEMS = [
  "Leave the squadron without telling a member of staff.",
  "Intimidate or bully other cadets.",
  "Behave in a disruptive manner.",
  "Keep your hands in your uniform pockets.",
  "Salute when not wearing your beret.",
  "Smoke or drink alcohol while on ATC duty or in uniform.",
  "Add or request staff or over-18 cadets as friends on personal social-media accounts.",
];

const UNIFORM_ISSUED = [
  "Trousers and/or a skirt",
  "Light blue and dark blue shirt",
  "Tie",
  "Jumper",
  "Beret",
  "Waterproof jacket",
  "Armband (brassard) for badges",
  "Coveralls when required",
];

const UNIFORM_SELF = [
  "Black parade shoes",
  "Black or brown combat boots",
  "Black socks and/or tights",
  "MTP combat clothing where required",
];

function Field({
  label,
  value,
  onChange,
  placeholder = "",
  multiline = false,
  rows = 4,
}) {
  const common =
    "mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-raf-blue focus:ring-2 focus:ring-blue-100";

  return (
    <label className="block">
      <span className="text-sm font-bold text-raf-navy">
        {label}
      </span>

      {multiline ? (
        <textarea
          rows={rows}
          value={value || ""}
          onChange={(event) =>
            onChange(event.target.value)
          }
          placeholder={placeholder}
          className={`${common} resize-y`}
        />
      ) : (
        <input
          type="text"
          value={value || ""}
          onChange={(event) =>
            onChange(event.target.value)
          }
          placeholder={placeholder}
          className={common}
        />
      )}
    </label>
  );
}

function ReadConfirmation({
  checked,
  onChange,
  children,
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={[
        "w-full text-left rounded-xl border-2 p-4 transition",
        checked
          ? "border-emerald-400 bg-emerald-50"
          : "border-slate-200 bg-white hover:border-raf-blue",
      ].join(" ")}
    >
      <div className="flex items-start gap-3">
        {checked ? (
          <CheckCircle2 className="w-6 h-6 text-emerald-700 shrink-0" />
        ) : (
          <Circle className="w-6 h-6 text-slate-400 shrink-0" />
        )}

        <div>
          <p className="font-bold text-raf-navy">
            {children}
          </p>

          <p className="text-sm text-slate-500 mt-1">
            Tap to {checked ? "remove" : "confirm"}.
          </p>
        </div>
      </div>
    </button>
  );
}

function InfoBox({
  title,
  children,
  tone = "blue",
  icon: Icon = BookOpen,
}) {
  const tones = {
    blue: "bg-blue-50 border-blue-200 text-blue-950",
    green:
      "bg-emerald-50 border-emerald-200 text-emerald-950",
    amber:
      "bg-amber-50 border-amber-200 text-amber-950",
    red: "bg-red-50 border-red-200 text-red-950",
  };

  return (
    <div
      className={`rounded-xl border p-4 ${tones[tone] || tones.blue}`}
    >
      <div className="flex items-start gap-3">
        <Icon className="w-5 h-5 shrink-0 mt-0.5" />

        <div>
          {title && (
            <h3 className="font-bold">
              {title}
            </h3>
          )}

          <div className="text-sm leading-relaxed mt-1">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

function Task1({
  answers,
  updateAnswer,
}) {
  return (
    <div className="space-y-5">
      <Intro
        icon={Users}
        title="About Your Squadron"
        text="Find out the key information about your squadron. Ask another cadet or a member of staff if you are unsure."
      />

      <div className="grid md:grid-cols-2 gap-4">
        <Field
          label="Squadron number and name"
          value={answers.squadronNumberName}
          onChange={(value) =>
            updateAnswer(
              "squadronNumberName",
              value
            )
          }
          placeholder="e.g. 1471 (Horwich) Squadron"
        />

        <Field
          label="Squadron address"
          value={answers.squadronAddress}
          onChange={(value) =>
            updateAnswer(
              "squadronAddress",
              value
            )
          }
        />

        <Field
          label="Phone number"
          value={answers.phoneNumber}
          onChange={(value) =>
            updateAnswer(
              "phoneNumber",
              value
            )
          }
        />

        <Field
          label="Email address"
          value={answers.emailAddress}
          onChange={(value) =>
            updateAnswer(
              "emailAddress",
              value
            )
          }
        />

        <Field
          label="Parade night days"
          value={answers.paradeNightDays}
          onChange={(value) =>
            updateAnswer(
              "paradeNightDays",
              value
            )
          }
        />

        <Field
          label="Parade night times"
          value={answers.paradeNightTimes}
          onChange={(value) =>
            updateAnswer(
              "paradeNightTimes",
              value
            )
          }
        />

        <Field
          label="Commanding Officer"
          value={answers.commandingOfficer}
          onChange={(value) =>
            updateAnswer(
              "commandingOfficer",
              value
            )
          }
        />

        <Field
          label="Padre"
          value={answers.padre}
          onChange={(value) =>
            updateAnswer(
              "padre",
              value
            )
          }
        />
      </div>

      <Field
        label="Other squadron staff and their roles"
        value={answers.otherStaff}
        onChange={(value) =>
          updateAnswer(
            "otherStaff",
            value
          )
        }
        multiline
        rows={5}
        placeholder="Name / role..."
      />
    </div>
  );
}

function Task2({
  answers,
  updateAnswer,
}) {
  return (
    <div className="space-y-5">
      <Intro
        icon={GraduationCap}
        title="Cadet Portal"
        text="Cadet Portal is the RAF Air Cadets information-sharing system. You should check it regularly."
      />

      <div className="grid md:grid-cols-2 gap-3">
        {[
          "View and sign up for squadron events",
          "Track badges, qualifications and awards",
          "Submit absences",
          "View your personal brassard",
          "Update your personal details",
          "Access information about uniform and drill",
          "Access welfare and support information",
        ].map((item) => (
          <div
            key={item}
            className="rounded-xl border border-slate-200 bg-white p-4 flex gap-3"
          >
            <CheckCircle2 className="w-5 h-5 text-raf-blue shrink-0" />
            <p className="text-sm text-slate-700">
              {item}
            </p>
          </div>
        ))}
      </div>

      <InfoBox
        title="Your username is not stored here"
        icon={ShieldCheck}
      >
        This local training system only records that you have been
        given access to Cadet Portal. It does not need to save your
        Cadet Portal username or password.
      </InfoBox>

      <a
        href="https://cadets.bader.mod.uk/"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 rounded-xl bg-raf-navy px-4 py-3 font-bold text-white"
      >
        <ExternalLink size={17} />
        Open Cadet Portal
      </a>

      <ReadConfirmation
        checked={Boolean(
          answers.cadetPortalConfirmed
        )}
        onChange={(value) =>
          updateAnswer(
            "cadetPortalConfirmed",
            value
          )
        }
      >
        I have been shown how to access Cadet Portal.
      </ReadConfirmation>
    </div>
  );
}

function Task3({
  answers,
  updateAnswer,
}) {
  return (
    <div className="space-y-5">
      <Intro
        icon={ShieldCheck}
        title="Squadron Do’s and Don’ts"
        text="RAF Air Cadets represent the Royal Air Force at many events, so high standards of behaviour and appearance are expected."
      />

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-emerald-200 overflow-hidden">
          <div className="bg-emerald-700 text-white px-4 py-3 font-black">
            Do
          </div>

          <ul className="p-4 space-y-2 bg-emerald-50">
            {DO_ITEMS.map((item) => (
              <li
                key={item}
                className="flex gap-2 text-sm text-emerald-950"
              >
                <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-xl border border-red-200 overflow-hidden">
          <div className="bg-red-700 text-white px-4 py-3 font-black">
            Don’t
          </div>

          <ul className="p-4 space-y-2 bg-red-50">
            {DONT_ITEMS.map((item) => (
              <li
                key={item}
                className="flex gap-2 text-sm text-red-950"
              >
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <Field
        label="Other things to remember at your squadron"
        value={answers.otherThingsToRemember}
        onChange={(value) =>
          updateAnswer(
            "otherThingsToRemember",
            value
          )
        }
        multiline
        rows={5}
        placeholder="Add any local squadron rules, procedures or reminders..."
      />
    </div>
  );
}

function Task4({
  answers,
  updateAnswer,
}) {
  return (
    <div className="space-y-5">
      <Intro
        icon={ShieldCheck}
        title="Keeping Safe in the RAF Air Cadets"
        text="Being an Air Cadet should be fun. You should feel safe and enjoy your cadet experience."
      />

      <InfoBox
        tone="green"
        title="If you have a problem"
        icon={ShieldCheck}
      >
        If something is making you feel unsafe, worried or unable to
        enjoy your cadet experience, speak to a member of staff. Staff
        are there to help and support you.
      </InfoBox>

      <ReadConfirmation
        checked={Boolean(
          answers.keepingSafeConfirmed
        )}
        onChange={(value) =>
          updateAnswer(
            "keepingSafeConfirmed",
            value
          )
        }
      >
        I know that I should speak to a member of staff if I feel
        unsafe or have a problem.
      </ReadConfirmation>
    </div>
  );
}

function Task5({
  answers,
  updateAnswer,
}) {
  return (
    <div className="space-y-5">
      <Intro
        icon={ShieldCheck}
        title="Health and Safety Induction"
        text="Everyone is responsible for Health and Safety. You must take care of yourself and others and report anything dangerous."
      />

      <div className="grid md:grid-cols-2 gap-3">
        {[
          "Report any danger.",
          "Take care of yourself and others.",
          "Use equipment in the way you have been trained.",
          "Do not block fire exits or doorways.",
          "Turn off equipment when it is not in use.",
          "Know where the first aid kit is and who the First Aider is.",
          "Know where the fire exits and fire assembly point are.",
        ].map((item) => (
          <div
            key={item}
            className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex gap-3"
          >
            <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0" />
            <p className="text-sm text-emerald-950">
              {item}
            </p>
          </div>
        ))}
      </div>

      <ReadConfirmation
        checked={Boolean(
          answers.safetyInductionConfirmed
        )}
        onChange={(value) =>
          updateAnswer(
            "safetyInductionConfirmed",
            value
          )
        }
      >
        I have received a first-day/parade safety induction covering
        Fire & Evacuation, First Aid, welfare facilities and security.
      </ReadConfirmation>

      <Field
        label="Health & Safety hazards specific to your squadron"
        value={answers.healthSafetyHazards}
        onChange={(value) =>
          updateAnswer(
            "healthSafetyHazards",
            value
          )
        }
        multiline
        rows={6}
        placeholder="Write the hazards you have been shown or discussed..."
      />
    </div>
  );
}

function Task6({
  answers,
  updateAnswer,
}) {
  return (
    <div className="space-y-5">
      <Intro
        icon={Flame}
        title="Fire Safety Induction"
        text="You should read your Squadron Fire Orders and know the immediate actions to take if there is a fire."
      />

      <div className="space-y-3">
        {[
          "If you discover a fire, shout FIRE clearly and sound the fire alarm.",
          "When the fire alarm sounds, leave by the nearest safe exit without running and go to the assembly point.",
          "Follow instructions from staff and senior cadets and make sure you are safe.",
          "If instructed to call 999, ask for the Fire Service, answer their questions, give the location and then report back to the senior member of staff.",
        ].map((item, index) => (
          <div
            key={item}
            className="rounded-xl border border-red-200 bg-red-50 p-4 flex gap-4"
          >
            <div className="w-8 h-8 rounded-full bg-red-700 text-white flex items-center justify-center font-black shrink-0">
              {index + 1}
            </div>

            <p className="text-sm text-red-950 leading-relaxed">
              {item}
            </p>
          </div>
        ))}
      </div>

      <Field
        label="Location of your unit's fire assembly point"
        value={answers.fireAssemblyPoint}
        onChange={(value) =>
          updateAnswer(
            "fireAssemblyPoint",
            value
          )
        }
        multiline
        rows={4}
        placeholder="Describe where you must assemble..."
      />

      <ReadConfirmation
        checked={Boolean(
          answers.fireSafetyConfirmed
        )}
        onChange={(value) =>
          updateAnswer(
            "fireSafetyConfirmed",
            value
          )
        }
      >
        I have been shown the fire alarm, fire exits and assembly
        point.
      </ReadConfirmation>
    </div>
  );
}

function Task7({
  answers,
  updateAnswer,
}) {
  return (
    <div className="space-y-5">
      <Intro
        icon={Sparkles}
        title="Initial Activities"
        text="Your first weeks introduce you to squadron life and the activities available across the RAF Air Cadets."
      />

      <InfoBox title="Your early training">
        You will learn about the Air Training Corps, how to wear and
        look after uniform, and practise drill. Your First Class
        training then develops subjects including the Royal Air Force,
        history of flight, outdoor expeditions, radio communications,
        map reading, airfields and flying.
      </InfoBox>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {[
          "Flying & gliding",
          "Sports",
          "Fieldcraft",
          "Shooting",
          "First aid",
          "Radio",
          "Cyber",
          "Space",
          "Camps",
          "Leadership",
          "Adventurous training",
          "Drill & ceremonial",
        ].map((item) => (
          <div
            key={item}
            className="rounded-xl border border-slate-200 bg-white p-4 font-semibold text-raf-navy"
          >
            {item}
          </div>
        ))}
      </div>

      <ReadConfirmation
        checked={Boolean(
          answers.initialActivitiesConfirmed
        )}
        onChange={(value) =>
          updateAnswer(
            "initialActivitiesConfirmed",
            value
          )
        }
      >
        I understand that I should try new activities and make the
        most of the opportunities available.
      </ReadConfirmation>
    </div>
  );
}

function Task8({
  answers,
  updateAnswer,
}) {
  return (
    <div className="space-y-5">
      <Intro
        icon={GraduationCap}
        title="About Your Uniform"
        text="Your RAF Air Cadets uniform should be worn with pride and looked after properly."
      />

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-blue-200 overflow-hidden">
          <div className="bg-raf-navy text-white px-4 py-3 font-black">
            Uniform normally issued
          </div>

          <ul className="p-4 space-y-2 bg-blue-50">
            {UNIFORM_ISSUED.map((item) => (
              <li
                key={item}
                className="flex gap-2 text-sm text-blue-950"
              >
                <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <div className="bg-slate-700 text-white px-4 py-3 font-black">
            Items you may need to obtain
          </div>

          <ul className="p-4 space-y-2 bg-slate-50">
            {UNIFORM_SELF.map((item) => (
              <li
                key={item}
                className="flex gap-2 text-sm text-slate-800"
              >
                <Circle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <InfoBox
        tone="amber"
        title="Look after it"
      >
        Keep your uniform clean and well pressed. Keep shoes clean and
        polished, and maintain the personal appearance standards you
        are taught by your squadron staff.
      </InfoBox>

      <ReadConfirmation
        checked={Boolean(
          answers.uniformConfirmed
        )}
        onChange={(value) =>
          updateAnswer(
            "uniformConfirmed",
            value
          )
        }
      >
        I understand that RAF Air Cadets uniform must be worn and
        looked after correctly.
      </ReadConfirmation>
    </div>
  );
}

function Intro({
  icon: Icon,
  title,
  text,
}) {
  return (
    <div>
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-raf-navy text-white flex items-center justify-center shrink-0">
          <Icon size={24} />
        </div>

        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">
            New Starter
          </p>

          <h2 className="text-2xl md:text-3xl font-black text-raf-navy mt-1">
            {title}
          </h2>

          <p className="text-slate-600 mt-2 leading-relaxed">
            {text}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function NewStarter() {
  const navigate = useNavigate();

  const {
    active,
    serviceNumber,
    activity,
    activityProgress,
    currentStep,
    sessionData,
    setCurrentStep,
    setSessionData,
    queueSave,
    saveState,
    updateModule,
    setActivityComplete,
    endSession,
    saving,
    ending,
    error,
  } = useTrainingSession();

  const answers =
    sessionData?.answers || {};

  const serverCompleted = useMemo(() => {
    const out = {};

    for (const module of
      activityProgress?.modules || []) {
      if (module?.status === "complete") {
        out[module.module_id] = true;
      }
    }

    return out;
  }, [activityProgress]);

  const completedTasks = useMemo(
    () => ({
      ...(sessionData?.completedTasks ||
        {}),
      ...serverCompleted,
    }),
    [
      serverCompleted,
      sessionData?.completedTasks,
    ]
  );

  const validCurrentStep =
    TASKS.some(
      (task) =>
        task.id === currentStep
    )
      ? currentStep
      : "task1";

  const [selectedTask, setSelectedTask] =
    useState(validCurrentStep);

  const [localError, setLocalError] =
    useState("");

  const [completing, setCompleting] =
    useState(false);

  useEffect(() => {
    if (
      !active ||
      !serviceNumber ||
      activity !== "newstarter"
    ) {
      navigate(
        "/training",
        { replace: true }
      );
    }
  }, [
    active,
    activity,
    navigate,
    serviceNumber,
  ]);

  useEffect(() => {
    if (
      currentStep &&
      TASKS.some(
        (task) =>
          task.id === currentStep
      )
    ) {
      setSelectedTask(currentStep);
    }
  }, [currentStep]);

  const updateAnswer =
    useCallback(
      (key, value) => {
        setSessionData(
          (previous) => ({
            ...previous,
            answers: {
              ...(previous?.answers ||
                {}),
              [key]: value,
            },
          })
        );

        queueSave();
      },
      [
        queueSave,
        setSessionData,
      ]
    );

  const goToTask =
    useCallback(
      async (taskId) => {
        setSelectedTask(taskId);
        setCurrentStep(taskId);
        setLocalError("");

        if (!completedTasks[taskId]) {
          updateModule(
            taskId,
            "in_progress"
          ).catch(() => {});
        }

        queueSave(250);
      },
      [
        completedTasks,
        queueSave,
        setCurrentStep,
        updateModule,
      ]
    );

  const validateTask =
    useCallback(
      (taskId) => {
        switch (taskId) {
          case "task1":
            if (
              !answers.squadronNumberName?.trim() ||
              !answers.squadronAddress?.trim() ||
              !answers.paradeNightDays?.trim() ||
              !answers.paradeNightTimes?.trim()
            ) {
              return "Complete the core squadron details before marking this task complete.";
            }
            return "";

          case "task2":
            return answers.cadetPortalConfirmed
              ? ""
              : "Confirm that you have been shown how to access Cadet Portal.";

          case "task3":
            return answers.otherThingsToRemember?.trim()
              ? ""
              : "Add at least one local squadron rule or reminder.";

          case "task4":
            return answers.keepingSafeConfirmed
              ? ""
              : "Confirm that you know who to speak to if you feel unsafe or have a problem.";

          case "task5":
            if (
              !answers.safetyInductionConfirmed
            ) {
              return "Confirm that you have received the Health & Safety induction.";
            }

            if (
              !answers.healthSafetyHazards?.trim()
            ) {
              return "Record the Health & Safety hazards discussed during your induction.";
            }

            return "";

          case "task6":
            if (
              !answers.fireAssemblyPoint?.trim()
            ) {
              return "Enter the location of the fire assembly point.";
            }

            return answers.fireSafetyConfirmed
              ? ""
              : "Confirm that you have been shown the fire alarm, exits and assembly point.";

          case "task7":
            return answers.initialActivitiesConfirmed
              ? ""
              : "Confirm that you understand the range of activities available.";

          case "task8":
            return answers.uniformConfirmed
              ? ""
              : "Confirm that you understand how your uniform should be worn and looked after.";

          default:
            return "";
        }
      },
      [answers]
    );

  const markCurrentComplete =
    useCallback(async () => {
      setLocalError("");

      const validation =
        validateTask(selectedTask);

      if (validation) {
        setLocalError(validation);
        return;
      }

      setCompleting(true);

      try {
        const nextCompleted = {
          ...completedTasks,
          [selectedTask]: true,
        };

        setSessionData(
          (previous) => ({
            ...previous,
            completedTasks:
              nextCompleted,
          })
        );

        await saveState(
          selectedTask,
          {
            ...sessionData,
            completedTasks:
              nextCompleted,
          }
        );

        await updateModule(
          selectedTask,
          "complete"
        );

        const allComplete =
          TASKS.every(
            (task) =>
              Boolean(
                nextCompleted[
                  task.id
                ]
              )
          );

        if (allComplete) {
          await setActivityComplete(
            true
          );
        }

        const currentIndex =
          TASKS.findIndex(
            (task) =>
              task.id ===
              selectedTask
          );

        const nextTask =
          TASKS[
            currentIndex + 1
          ];

        if (nextTask) {
          await goToTask(
            nextTask.id
          );
        }
      } catch (err) {
        setLocalError(
          err?.message ||
            "Unable to save this task."
        );
      } finally {
        setCompleting(false);
      }
    }, [
      completedTasks,
      goToTask,
      saveState,
      selectedTask,
      sessionData,
      setActivityComplete,
      setSessionData,
      updateModule,
      validateTask,
    ]);

  const previousTask = () => {
    const index =
      TASKS.findIndex(
        (task) =>
          task.id ===
          selectedTask
      );

    if (index > 0) {
      goToTask(
        TASKS[index - 1].id
      );
    }
  };

  const nextTask = () => {
    const index =
      TASKS.findIndex(
        (task) =>
          task.id ===
          selectedTask
      );

    if (
      index >= 0 &&
      index < TASKS.length - 1
    ) {
      goToTask(
        TASKS[index + 1].id
      );
    }
  };

  const endAndSave = async () => {
    setLocalError("");

    try {
      await endSession();

      navigate(
        "/training",
        { replace: true }
      );
    } catch (err) {
      setLocalError(
        err?.message ||
          "Unable to end the session."
      );
    }
  };

  const completedCount =
    TASKS.filter(
      (task) =>
        Boolean(
          completedTasks[
            task.id
          ]
        )
    ).length;

  const progress =
    Math.round(
      (completedCount /
        TASKS.length) *
        100
    );

  const currentTask =
    TASKS.find(
      (task) =>
        task.id ===
        selectedTask
    ) || TASKS[0];

  const renderTask = () => {
    switch (selectedTask) {
      case "task1":
        return (
          <Task1
            answers={answers}
            updateAnswer={
              updateAnswer
            }
          />
        );

      case "task2":
        return (
          <Task2
            answers={answers}
            updateAnswer={
              updateAnswer
            }
          />
        );

      case "task3":
        return (
          <Task3
            answers={answers}
            updateAnswer={
              updateAnswer
            }
          />
        );

      case "task4":
        return (
          <Task4
            answers={answers}
            updateAnswer={
              updateAnswer
            }
          />
        );

      case "task5":
        return (
          <Task5
            answers={answers}
            updateAnswer={
              updateAnswer
            }
          />
        );

      case "task6":
        return (
          <Task6
            answers={answers}
            updateAnswer={
              updateAnswer
            }
          />
        );

      case "task7":
        return (
          <Task7
            answers={answers}
            updateAnswer={
              updateAnswer
            }
          />
        );

      case "task8":
        return (
          <Task8
            answers={answers}
            updateAnswer={
              updateAnswer
            }
          />
        );

      default:
        return null;
    }
  };

  if (
    !active ||
    !serviceNumber
  ) {
    return (
      <div className="min-h-screen bg-raf-navy text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-raf-navy text-white border-b-4 border-raf-red">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center">
                <Sparkles size={23} />
              </div>

              <div>
                <p className="text-xs uppercase tracking-widest text-white/60 font-bold">
                  1471 Horwich Squadron
                </p>

                <h1 className="text-xl md:text-2xl font-black">
                  New Starter
                </h1>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="rounded-lg bg-white/10 px-3 py-2">
                <p className="text-[10px] uppercase tracking-widest text-white/50">
                  Training record
                </p>

                <p className="font-bold tracking-wide">
                  {formatServiceNumber(
                    serviceNumber
                  )}
                </p>
              </div>

              <button
                type="button"
                onClick={endAndSave}
                disabled={
                  ending || saving
                }
                className="inline-flex items-center gap-2 rounded-lg bg-raf-red px-4 py-3 font-bold disabled:opacity-50"
              >
                {ending ? (
                  <Loader2
                    size={17}
                    className="animate-spin"
                  />
                ) : (
                  <Save size={17} />
                )}

                End Session & Save
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-5">
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <p className="font-bold text-raf-navy">
                Induction progress
              </p>

              <p className="text-sm text-slate-500">
                {completedCount} of{" "}
                {TASKS.length} tasks complete
              </p>
            </div>

            <p className="text-2xl font-black text-raf-navy">
              {progress}%
            </p>
          </div>

          <div className="mt-3 h-3 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full bg-raf-blue transition-all duration-300"
              style={{
                width: `${progress}%`,
              }}
            />
          </div>

          {progress === 100 && (
            <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-900 font-semibold">
              All eight New Starter tasks are complete.
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-[270px_minmax(0,1fr)] gap-5">
          <aside className="lg:sticky lg:top-4 lg:self-start">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                <p className="text-xs uppercase tracking-widest font-bold text-slate-500">
                  Induction checklist
                </p>
              </div>

              <div className="p-2">
                {TASKS.map((task) => {
                  const done =
                    Boolean(
                      completedTasks[
                        task.id
                      ]
                    );

                  const activeTask =
                    task.id ===
                    selectedTask;

                  return (
                    <button
                      key={task.id}
                      type="button"
                      onClick={() =>
                        goToTask(
                          task.id
                        )
                      }
                      className={[
                        "w-full rounded-lg p-3 text-left transition flex items-center gap-3",
                        activeTask
                          ? "bg-raf-navy text-white"
                          : "hover:bg-slate-50 text-slate-700",
                      ].join(" ")}
                    >
                      {done ? (
                        <CheckCircle2
                          size={20}
                          className={
                            activeTask
                              ? "text-emerald-300"
                              : "text-emerald-600"
                          }
                        />
                      ) : (
                        <Circle
                          size={20}
                          className={
                            activeTask
                              ? "text-white/60"
                              : "text-slate-300"
                          }
                        />
                      )}

                      <div className="min-w-0">
                        <p className="text-xs font-bold opacity-60">
                          Task{" "}
                          {task.number}
                        </p>

                        <p className="font-semibold leading-tight">
                          {task.short}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>

          <main className="min-w-0">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
              <div className="px-5 md:px-7 py-4 border-b border-slate-200 flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-widest font-bold">
                    Task{" "}
                    {currentTask.number} of{" "}
                    {TASKS.length}
                  </p>

                  <p className="font-bold text-raf-navy mt-1">
                    {completedTasks[
                      selectedTask
                    ]
                      ? "Completed"
                      : "In progress"}
                  </p>
                </div>

                {saving && (
                  <div className="inline-flex items-center gap-2 text-sm text-slate-500">
                    <Loader2
                      size={16}
                      className="animate-spin"
                    />
                    Saving...
                  </div>
                )}
              </div>

              <div className="p-5 md:p-7">
                {renderTask()}
              </div>

              {(localError || error) && (
                <div className="mx-5 md:mx-7 mb-5 rounded-xl border border-red-200 bg-red-50 p-4 flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />

                  <p className="text-sm text-red-800">
                    {localError ||
                      error}
                  </p>
                </div>
              )}

              <div className="border-t border-slate-200 p-4 md:p-5">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <button
                    type="button"
                    onClick={previousTask}
                    disabled={
                      currentTask.number ===
                      1
                    }
                    className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 px-4 py-3 font-bold text-raf-navy disabled:opacity-40"
                  >
                    <ChevronLeft
                      size={18}
                    />
                    Previous
                  </button>

                  <button
                    type="button"
                    onClick={
                      markCurrentComplete
                    }
                    disabled={
                      completing ||
                      Boolean(
                        completedTasks[
                          selectedTask
                        ]
                      )
                    }
                    className={[
                      "inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 font-black",
                      completedTasks[
                        selectedTask
                      ]
                        ? "bg-emerald-100 text-emerald-800"
                        : "bg-raf-red text-white",
                      "disabled:opacity-70",
                    ].join(" ")}
                  >
                    {completing ? (
                      <Loader2
                        size={18}
                        className="animate-spin"
                      />
                    ) : completedTasks[
                        selectedTask
                      ] ? (
                      <CheckCircle2
                        size={18}
                      />
                    ) : (
                      <CheckCircle2
                        size={18}
                      />
                    )}

                    {completedTasks[
                      selectedTask
                    ]
                      ? "Task Complete"
                      : "Mark Task Complete"}
                  </button>

                  <button
                    type="button"
                    onClick={nextTask}
                    disabled={
                      currentTask.number ===
                      TASKS.length
                    }
                    className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 px-4 py-3 font-bold text-raf-navy disabled:opacity-40"
                  >
                    Next
                    <ChevronRight
                      size={18}
                    />
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-center">
              <button
                type="button"
                onClick={endAndSave}
                className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-raf-navy"
              >
                <Home size={16} />
                Finish on this tablet
              </button>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}